/**
 * « Préparer mon appel » — la préparation complète, en une seule fenêtre.
 *
 * Elle a absorbé l'onglet « Préparer une action » : celui-ci demandait au
 * commercial de choisir une action, un contexte et un modèle, c'est-à-dire le
 * vocabulaire du CRM, alors qu'il pense en objectif — « je veux un
 * rendez-vous ». Ici, l'objectif choisit le modèle (côté serveur,
 * crm/copilot/objectives.py), le modèle produit le discours, et le discours est
 * affiché tel qu'il doit être lu.
 *
 * L'enchaînement du parcours réel :
 *   1. objectif → modèle → discours d'ouverture, prêt à lire à la lettre ;
 *   2. « Appeler » : la fenêtre d'appel s'ouvre en semi plein écran avec le
 *      discours ÉPINGLÉ au milieu — une seule fenêtre, plus de « Ma
 *      préparation » à côté ;
 *   3. le client répond : les suggestions prennent le relais au même endroit,
 *      priorisées selon le playbook de l'équipe.
 */
import { useState } from "react";
import { toast } from "sonner";
import {
  Sparkles, PhoneCall, X, RefreshCw, Copy, Check, Pencil, Save, Brain,
} from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import { useTwilio } from "@/context/TwilioVoice";
import Modal from "@/crm/components/Modal";
import ContactPicker, { callTargets, needsChoice } from "@/crm/components/ContactPicker";

export default function PrepareCallButton({ company }) {
  const tw = useTwilio();
  // "closed" : rien à l'écran · "prepare" : fenêtre de préparation.
  const [phase, setPhase] = useState("closed");
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [prepared, setPrepared] = useState(null);
  const [objective, setObjective] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [discourse, setDiscourse] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [copied, setCopied] = useState(false);
  const [scriptError, setScriptError] = useState("");

  /** Charge (ou recharge) la préparation pour un objectif / un modèle / une
   *  solution donnés. Sans `solution_id`, on garde la solution de la
   *  préparation en cours ; la première fois, le serveur choisit (fiche,
   *  sinon la solution adaptée à l'activité). */
  const prepare = async ({ objective: obj, template_id: tpl, solution_id: sol, open = true } = {}) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/copilot/prepare/${company.id}`, {
        objective: obj ?? objective ?? "",
        template_id: tpl,
        solution_id: sol === undefined ? prepared?.solution?.id : (sol || null),
      });
      setPrepared(data);
      setObjective(data.objective);
      setTemplateId(data.template_id || "");
      setDiscourse(data.discourse || null);
      setEditing(false);
      if (open) setPhase("prepare");
      // Aucun discours pour ce couple objectif/modèle : on le rédige tout de
      // suite. Le commercial ouvre cette fenêtre pour lire un texte, pas pour
      // cliquer sur « générer ».
      if (!data.discourse && data.template_id) generate(data, { silent: true });
      return data;
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "object" ? (detail.message || "Préparation impossible")
                                             : apiError(detail));
      return null;
    } finally { setLoading(false); }
  };

  /**
   * Rédige le discours. `silent` pour la génération automatique à l'ouverture :
   * un échec s'affiche alors dans le cadre du discours, là où le commercial
   * regarde, plutôt qu'en toast à chaque ouverture de la fenêtre.
   */
  const generate = async (base, { silent = false } = {}) => {
    const source = base || prepared;
    const tpl = base ? base.template_id : templateId;
    if (!source || !tpl) return;
    setGenerating(true);
    setScriptError("");
    try {
      const { data } = await api.post(`/companies/${company.id}/generate`, {
        action_type: source.action_type, context: source.call_context,
        template_id: tpl, use_site_analysis: Boolean(company.site_analysis),
        solution_id: source.solution?.id || null,
      });
      setDiscourse(data);
      setEditing(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const message = typeof detail === "object" ? (detail.message || "Génération impossible")
                                                 : apiError(detail);
      setScriptError(message);
      if (!silent) toast.error(message);
    } finally { setGenerating(false); }
  };

  /** Retient la solution sur la fiche, puis recharge la préparation avec elle :
   *  discours, copilote et fiche doivent dire la même chose. */
  const chooseSolution = async (id) => {
    try { await api.put(`/companies/${company.id}/solution`, { solution_id: id || null }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); return; }
    prepare({ solution_id: id || "" });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/discourses/${discourse.id}`, { final_text: draft });
      setDiscourse((d) => ({ ...d, final_text: draft, edited: true }));
      setEditing(false);
      toast.success("Discours enregistré");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(discourse?.final_text || "");
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Copie impossible — sélectionnez le texte"); }
  };

  const [picking, setPicking] = useState(false);
  const launch = (target) => {
    // La fenêtre d'appel (LiveCopilot) s'ouvre en semi plein écran et affiche
    // elle-même le discours, épinglé jusqu'à la fin de la lecture : la
    // préparation peut se fermer, rien ne disparaît des yeux du commercial.
    setPicking(false);
    setPhase("closed");
    tw?.makeCall(target.number, {
      company_id: company.id, company_name: company.nom, objective,
      contact_id: target.contact_id || "",
      // La solution proposée voyage avec l'appel : le copilote live la reçoit
      // par le fournisseur, comme l'objectif.
      solution_id: prepared?.solution?.id || "",
    });
  };
  const call = () => {
    // Standard + contacts joignables : on choisit la personne avant de composer.
    if (needsChoice(company)) { setPicking(true); return; }
    const [seul] = callTargets(company);
    if (seul) launch(seul);
  };

  const ctx = prepared?.context;
  const memory = prepared?.client_memory;
  const choices = prepared?.solutions;
  const hasSolutions = (choices?.applicable?.length || 0) + (choices?.autres?.length || 0) > 0;
  const known = (memory?.key_points || []).length > 0 || memory?.calls_count > 0;
  const script = discourse?.final_text || "";

  return (
    <>
      <button data-testid="prepare-call-btn" onClick={() => prepare({})}
        disabled={loading || company.excluded || callTargets(company).length === 0}
        title={callTargets(company).length ? "Préparer l'appel avec le copilote" : "Aucun numéro"}
        className="flex items-center gap-2 rounded-xl border border-indigo-100 bg-indigo-50/60 px-3.5 py-2
                   text-sm font-medium text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed">
        {loading ? <RefreshCw size={15} className="animate-spin" /> : <Sparkles size={15} />}
        Préparer mon appel
      </button>

      {phase === "prepare" && ctx && (
        <Modal onClose={() => setPhase("closed")} className="max-w-[620px]"
          testid="prepare-call-modal" label="Préparer l'appel">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-heading text-[17px] font-bold">Préparer l'appel</h3>
              <p className="mt-1 text-[13px] text-slate-500">{company.nom}</p>
            </div>
            <button onClick={() => setPhase("closed")} className="text-slate-400 hover:text-slate-900">
              <X size={18} />
            </button>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block text-[13px] font-medium text-slate-600">
              Objectif de l'appel
              <select data-testid="prepare-objective" value={objective} disabled={loading}
                onChange={(e) => prepare({ objective: e.target.value })}
                className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-600">
                {(prepared.objectives || []).map((o) => (
                  <option key={o.code} value={o.label}>{o.label}</option>
                ))}
              </select>
            </label>
            <label className="block text-[13px] font-medium text-slate-600">
              Modèle de discours
              <select data-testid="prepare-template" value={templateId} disabled={loading || generating}
                onChange={(e) => { setTemplateId(e.target.value); prepare({ template_id: e.target.value }); }}
                className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-600">
                {(prepared.templates || []).length === 0 && <option value="">Aucun modèle</option>}
                {(prepared.templates || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
          </div>

          {/* LA SOLUTION À PROPOSER — le discours et le copilote s'y adaptent */}
          {hasSolutions && (
            <label className="mt-3 block text-[13px] font-medium text-slate-600">
              Solution à proposer
              <select data-testid="prepare-solution" value={choices.retained_id || ""} disabled={loading || generating}
                onChange={(e) => chooseSolution(e.target.value)}
                className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-600">
                <option value="">
                  {choices.picked ? `Automatique — ${choices.picked.nom}` : "Automatique — aucune solution adaptée"}
                </option>
                {choices.applicable.length > 0 && (
                  <optgroup label="Adaptées à l'activité">
                    {choices.applicable.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
                  </optgroup>
                )}
                {choices.autres.length > 0 && (
                  <optgroup label="Autres solutions">
                    {choices.autres.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
                  </optgroup>
                )}
              </select>
              {prepared.solution?.contexte && (
                <span data-testid="prepare-solution-context" className="mt-1 block text-[12px] font-normal text-slate-500 text-pretty">
                  {prepared.solution.contexte}
                </span>
              )}
            </label>
          )}

          {/* CE QU'ON SAIT DÉJÀ — la mémoire des appels précédents */}
          {known && (
            <div data-testid="prepare-memory" className="mt-4 rounded-xl border border-violet-100 bg-violet-50/50 p-3.5">
              <div className="flex items-center gap-1.5 text-[12.5px] font-medium text-violet-800">
                <Brain size={14} />
                Ce que l'on sait déjà · {memory.calls_count || 0} appel{(memory.calls_count || 0) > 1 ? "s" : ""}
              </div>
              {memory.summary && <p className="mt-1.5 text-[12.5px] text-violet-900/80">{memory.summary}</p>}
              <ul className="mt-1.5 space-y-1">
                {(memory.key_points || []).map((point, i) => (
                  <li key={i} className="text-[12.5px] text-violet-900/80">• {point}</li>
                ))}
              </ul>
            </div>
          )}

          {/* LE DISCOURS À LIRE */}
          <div className="mt-4">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-[13px] font-medium text-slate-600">
                Discours d'ouverture — à lire tel quel
              </span>
              {discourse && (
                <span className="font-mono text-[11px] text-slate-400">
                  v{discourse.version}{discourse.edited ? " · modifié" : ""}
                </span>
              )}
            </div>

            {generating ? (
              <p className="flex items-center gap-2 rounded-xl border border-slate-100 bg-slate-50/70 px-3.5 py-6 text-[13px] text-slate-500">
                <RefreshCw size={14} className="animate-spin" /> Rédaction du discours…
              </p>
            ) : editing ? (
              <textarea data-testid="prepare-script-edit" value={draft} rows={8}
                onChange={(e) => setDraft(e.target.value)}
                className="w-full rounded-xl border border-slate-200 p-3 text-[14px] leading-relaxed outline-none focus:border-indigo-500" />
            ) : scriptError && !script ? (
              <div data-testid="prepare-script-error"
                className="rounded-xl border border-amber-100 bg-amber-50/70 p-3.5 text-[13px] text-amber-800">
                Discours indisponible — {scriptError}
                <button onClick={() => generate()}
                  className="ml-2 font-medium underline hover:no-underline">Réessayer</button>
              </div>
            ) : (
              <p data-testid="prepare-script"
                className="max-h-[240px] overflow-y-auto whitespace-pre-wrap rounded-xl border border-slate-100 bg-slate-50/70 p-3.5 text-[14px] leading-relaxed text-slate-800">
                {script || "Aucun discours disponible — choisissez un modèle."}
              </p>
            )}

            <div className="mt-2 flex flex-wrap gap-2">
              {editing ? (
                <button onClick={saveEdit}
                  className="flex items-center gap-1.5 rounded-lg bg-emerald-700 px-3 py-1.5 text-[12.5px] text-white hover:bg-emerald-800">
                  <Save size={13} /> Enregistrer
                </button>
              ) : (
                <>
                  <button onClick={copy} disabled={!script}
                    className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-[12.5px] text-slate-600 hover:border-slate-300 disabled:opacity-40">
                    {copied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
                    {copied ? "Copié" : "Copier"}
                  </button>
                  <button onClick={() => { setDraft(script); setEditing(true); }} disabled={!discourse}
                    className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-[12.5px] text-slate-600 hover:border-slate-300 disabled:opacity-40">
                    <Pencil size={13} /> Modifier
                  </button>
                  <button data-testid="prepare-regenerate" onClick={() => generate()}
                    disabled={generating || !templateId}
                    className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-[12.5px] text-slate-600 hover:border-slate-300 disabled:opacity-40">
                    <RefreshCw size={13} /> Régénérer
                  </button>
                </>
              )}
            </div>
          </div>

          <ContextRows ctx={ctx} />

          <p className="mt-3 text-[12.5px] text-slate-500">
            Lisez le discours à l'ouverture, puis laissez parler le client : le copilote
            prend le relais avec des suggestions priorisées. Il ne parle jamais au client.
          </p>

          <div className="mt-4 flex gap-2">
            <button onClick={() => setPhase("closed")}
              className="flex-1 rounded-xl border border-slate-200 py-2.5 text-sm text-slate-600 hover:border-slate-300">
              Annuler
            </button>
            <button data-testid="prepare-call-start" onClick={call}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-700 py-2.5
                         text-sm font-medium text-white hover:bg-emerald-800">
              <PhoneCall size={15} /> Appeler
            </button>
          </div>
        </Modal>
      )}

      {picking && (
        <ContactPicker company={company} title="Qui appeler avec ce discours ?"
          onClose={() => setPicking(false)} onPick={launch} />
      )}
    </>
  );
}

function ContextRows({ ctx }) {
  return (
    <div className="mt-4 space-y-2 rounded-xl border border-slate-100 bg-slate-50/70 p-3.5">
      <Row label="Statut" value={ctx.prospect?.statut} />
      <Row label="Score" value={ctx.prospect?.score} mono />
      <Row label="Secteur" value={ctx.company?.secteur} />
      <Row label="Solution" value={ctx.seller?.solution} />
      <Row label="Dernier appel" value={ctx.conversation?.summary || ctx.prospect?.tags} />
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="grid grid-cols-[104px_minmax(0,1fr)] items-baseline gap-2">
      <span className="text-[12.5px] text-slate-400">{label}</span>
      <span className={`text-[13.5px] text-slate-700 ${mono ? "font-mono" : ""}`}>{value || "—"}</span>
    </div>
  );
}
